import {
  currentSessionResponseSchema,
  loginRequestSchema,
  loginResponseSchema,
  type CurrentSessionResponse,
  type LoginRequest,
  type LoginResponse,
} from "@brainics/api-contract";

interface ResponseParser<T> {
  parse(value: unknown): T;
}

interface ApiErrorBody {
  code: string;
  message: string;
  requestId?: string;
}

export class ApiClientError extends Error {
  readonly status: number;
  readonly code: string;
  readonly requestId: string | undefined;

  constructor(status: number, body: ApiErrorBody) {
    super(body.message);
    this.name = "ApiClientError";
    this.status = status;
    this.code = body.code;
    this.requestId = body.requestId;
  }
}

function readApiError(value: unknown): ApiErrorBody | null {
  if (typeof value !== "object" || value === null || !("error" in value)) return null;

  const error = value.error;
  if (typeof error !== "object" || error === null) return null;
  if (!("code" in error) || typeof error.code !== "string") return null;
  if (!("message" in error) || typeof error.message !== "string") return null;

  const requestId =
    "requestId" in error && typeof error.requestId === "string" ? error.requestId : null;

  return {
    code: error.code,
    message: error.message,
    ...(requestId ? { requestId } : {}),
  };
}

async function apiErrorFromResponse(response: Response): Promise<ApiClientError> {
  let body: unknown;

  try {
    body = await response.json();
  } catch {
    body = null;
  }

  return new ApiClientError(
    response.status,
    readApiError(body) ?? {
      code: "HTTP_ERROR",
      message: `Request failed with status ${response.status}`,
    },
  );
}

export async function requestJson<T>(
  path: string,
  parser: ResponseParser<T>,
  init: RequestInit = {},
): Promise<T> {
  let response: Response;
  const headers = new Headers(init.headers);
  headers.set("accept", "application/json");

  try {
    response = await fetch(path, {
      ...init,
      credentials: "include",
      headers,
    });
  } catch {
    throw new ApiClientError(0, {
      code: "NETWORK_ERROR",
      message: "Network request failed",
    });
  }

  if (!response.ok) throw await apiErrorFromResponse(response);
  return parser.parse(await response.json());
}

export async function login(input: LoginRequest): Promise<LoginResponse> {
  const payload = loginRequestSchema.parse(input);

  return requestJson("/api/v1/auth/login", loginResponseSchema, {
    method: "POST",
    headers: { "content-type": "application/json" },
    body: JSON.stringify(payload),
  });
}

export async function getCurrentSession(): Promise<CurrentSessionResponse> {
  return requestJson("/api/v1/auth/session", currentSessionResponseSchema);
}

export async function logout(): Promise<void> {
  let response: Response;

  try {
    response = await fetch("/api/v1/auth/logout", {
      method: "POST",
      credentials: "include",
      headers: { accept: "application/json" },
    });
  } catch {
    throw new ApiClientError(0, {
      code: "NETWORK_ERROR",
      message: "Network request failed",
    });
  }

  if (!response.ok) throw await apiErrorFromResponse(response);
}

export function authErrorMessage(error: unknown): string {
  if (!(error instanceof ApiClientError)) {
    return "Не удалось выполнить запрос. Попробуйте ещё раз.";
  }

  switch (error.code) {
    case "INVALID_CREDENTIALS":
      return "Неверный email или пароль.";
    case "EMAIL_VERIFICATION_REQUIRED":
      return "Подтвердите email перед входом в кабинет.";
    case "ACCOUNT_TEMPORARILY_LOCKED":
      return "Слишком много попыток входа. Повторите позже.";
    case "ACCOUNT_UNAVAILABLE":
      return "Учётная запись недоступна. Обратитесь в поддержку.";
    case "RATE_LIMIT_EXCEEDED":
      return "Слишком много запросов. Повторите позже.";
    case "ORIGIN_NOT_ALLOWED":
    case "CROSS_SITE_REQUEST_REJECTED":
      return "Домен не разрешён для авторизации. Обратитесь в поддержку.";
    case "NETWORK_ERROR":
      return "Нет соединения с сервером. Проверьте интернет и повторите попытку.";
    default:
      return "Не удалось войти. Попробуйте ещё раз.";
  }
}
